/**
 * HYPSEYA — Emotional Timeline
 * Chronological view of sessions, journal entries, and (optionally) microdose logs.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
import orbUrl from '../orbAsset';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Mini sparkline ──────────────────────────────────────────────────────────
function Sparkline({ points, width = 280, height = 48 }) {
  if (!points || points.length < 2) return null;
  const vals = points.map(p => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const range = max - min || 1;
  const pad = 4;
  const w = width - pad * 2;
  const h = height - pad * 2;
  const pts = points.map((p, i) => {
    const x = pad + (i / (points.length - 1)) * w;
    const y = pad + h - ((p.value - min) / range) * h;
    return `${x},${y}`;
  });
  const last = pts[pts.length - 1].split(',');
  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id="spark-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#1E6B68" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#1E6B68" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`${pad},${height} ${pts.join(' ')} ${pad + w},${height}`}
        fill="url(#spark-grad)"
      />
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke="#1E6B68"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill="#1E6B68" />
    </svg>
  );
}

// ── Event node ──────────────────────────────────────────────────────────────
function EventNode({ event, isLast }) {
  const [expanded, setExpanded] = useState(false);

  const config = {
    session: {
      color: '#1E6B68',
      bg: 'rgba(30,107,104,0.08)',
      border: 'rgba(30,107,104,0.22)',
      icon: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" stroke="#1E6B68" strokeWidth="1.4"/>
          <path d="M4 7l2 2 4-4" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      label: 'Session',
    },
    journal: {
      color: '#4AACA8',
      bg: 'rgba(74,172,168,0.07)',
      border: 'rgba(74,172,168,0.2)',
      icon: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <rect x="2" y="1.5" width="8" height="11" rx="1.5" stroke="#4AACA8" strokeWidth="1.3"/>
          <line x1="4.5" y1="5" x2="8" y2="5" stroke="#4AACA8" strokeWidth="1.1" strokeLinecap="round"/>
          <line x1="4.5" y1="7" x2="8" y2="7" stroke="#4AACA8" strokeWidth="1.1" strokeLinecap="round"/>
          <line x1="4.5" y1="9" x2="6.5" y2="9" stroke="#4AACA8" strokeWidth="1.1" strokeLinecap="round"/>
        </svg>
      ),
      label: 'Journal',
    },
    microdose: {
      color: '#8B6BAE',
      bg: 'rgba(139,107,174,0.07)',
      border: 'rgba(139,107,174,0.2)',
      icon: (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="5.5" stroke="#8B6BAE" strokeWidth="1.3"/>
          <circle cx="7" cy="7" r="2" fill="#8B6BAE" opacity="0.7"/>
        </svg>
      ),
      label: event.is_dose_day ? 'Dose day' : 'Rest day',
    },
  };

  const c = config[event.type] || config.session;
  const dateLabel = new Date(event.date).toLocaleDateString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
  });

  return (
    <div style={{ display: 'flex', gap: 0, position: 'relative' }}>
      {/* Timeline spine */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: c.bg, border: `1.5px solid ${c.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, zIndex: 1,
        }}>
          {c.icon}
        </div>
        {!isLast && (
          <div style={{
            width: 1.5, flex: 1, minHeight: 16,
            background: 'rgba(184,211,207,0.35)', margin: '3px 0',
          }} />
        )}
      </div>

      {/* Content */}
      <div
        onClick={() => event.type === 'journal' && event.preview && setExpanded(x => !x)}
        style={{
          flex: 1, marginLeft: 10, marginBottom: isLast ? 0 : 12,
          padding: '12px 14px',
          background: expanded ? c.bg : 'rgba(255,255,255,0.75)',
          border: `1px solid ${expanded ? c.border : 'rgba(184,211,207,0.3)'}`,
          borderRadius: 14,
          cursor: event.type === 'journal' && event.preview ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span style={{
              fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.07em',
              textTransform: 'uppercase', color: c.color,
            }}>{c.label}</span>
            {event.type === 'session' && event.eri_delta !== null && event.eri_delta !== undefined && (
              <span style={{
                fontSize: '0.68rem',
                color: event.eri_delta > 0 ? '#1E6B68' : event.eri_delta < 0 ? '#B8906A' : '#AAB8B6',
                fontWeight: '600',
              }}>
                {event.eri_delta > 0 ? `↑ +${Math.round(event.eri_delta)}` : event.eri_delta < 0 ? `↓ ${Math.round(event.eri_delta)}` : '→ stable'}
              </span>
            )}
            {event.type === 'session' && event.stages_done > 0 && (
              <span style={{ fontSize: '0.65rem', color: '#AAB8B6' }}>
                {event.stages_done}/4 stages
              </span>
            )}
            {event.type === 'microdose' && !event.is_dose_day && (
              <span style={{ fontSize: '0.65rem', color: '#AAB8B6' }}>rest</span>
            )}
          </div>
          <span style={{ fontSize: '0.72rem', color: '#AABAB8' }}>{dateLabel}</span>
        </div>

        {event.type === 'session' && event.orient_theme && (
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#5A8A87', lineHeight: 1.4 }}>
            "{event.orient_theme}"
          </p>
        )}

        {event.type === 'journal' && event.preview && (
          <>
            <p style={{
              margin: 0, fontSize: '0.82rem', color: '#5A8A87', lineHeight: 1.45,
              display: '-webkit-box', WebkitLineClamp: expanded ? 'none' : 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {event.preview}
            </p>
            {!expanded && event.preview.length > 80 && (
              <span style={{ fontSize: '0.72rem', color: c.color, marginTop: 2, display: 'block' }}>read more →</span>
            )}
          </>
        )}

        {event.type === 'microdose' && event.is_dose_day && (
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#8B6BAE', lineHeight: 1.4 }}>
            Dose day · {event.protocol || 'Active protocol'}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Date group ──────────────────────────────────────────────────────────────
function DateGroup({ dateStr, events }) {
  const d = new Date(dateStr);
  const today = new Date().toISOString().split('T')[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
  const label = dateStr === today ? 'Today'
    : dateStr === yesterday ? 'Yesterday'
    : d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{
        fontSize: '0.7rem', color: '#7A9E9B', fontWeight: '700',
        letterSpacing: '0.06em', textTransform: 'uppercase',
        marginBottom: 10, paddingLeft: 42,
      }}>
        {label}
      </div>
      {events.map((ev, i) => (
        <EventNode key={`${dateStr}-${i}`} event={ev} isLast={i === events.length - 1} />
      ))}
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function EmotionalTimeline() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [user] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } });
  const [days, setDays] = useState(30);
  const [events, setEvents] = useState([]);
  const [sparkData, setSparkData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // Only show microdose section if user opted in
  const showMicrodose = Boolean(user?.microdosing_enabled);

  const authCfg = async () => {
    try { const t = await getAccessTokenSilently(); return { headers: { Authorization: `Bearer ${t}` } }; }
    catch { return {}; }
  };

  useEffect(() => {
    if (user?.user_id) load();
  }, [days, user?.user_id]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    const cfg = await authCfg();
    const email = localStorage.getItem('userEmail') || user?.email;
    try {
      const promises = [
        axios.get(`${API}/training/history/${user.user_id}?days=${days}`, cfg),
        axios.get(`${API}/journal-entries?user_id=${user.user_id}`, cfg),
      ];
      if (showMicrodose && email) {
        promises.push(axios.get(`${API}/microdosing-calendar/${email}?days=${days}`, cfg));
      }

      const results = await Promise.allSettled(promises);
      const [sessRes, jrnRes, mRes] = results;

      const allEvents = [];

      // Sessions
      if (sessRes.status === 'fulfilled') {
        const sessions = sessRes.value.data || [];
        sessions.forEach(s => {
          const stagesDone = [s.orient_completed, s.practice_completed, s.observe_completed, s.integrate_completed].filter(Boolean).length;
          if (stagesDone > 0) {
            allEvents.push({
              type: 'session',
              date: s.date,
              stages_done: stagesDone,
              orient_theme: s.orient_selection || s.orient_text || null,
              eri_delta: s.eri_delta ?? null,
            });
          }
        });

        // Build sparkline from session ERI data
        const sorted = [...sessions].sort((a, b) => a.date > b.date ? 1 : -1);
        const spark = [];
        let running = 0;
        sorted.forEach(s => {
          if (s.post_activation !== undefined && s.post_activation !== null) {
            running = s.post_activation;
          }
          if (s.orient_completed) {
            spark.push({ date: s.date, value: s.pre_activation ?? running ?? 50 });
          }
        });
        setSparkData(spark);
      }

      // Journal
      if (jrnRes.status === 'fulfilled') {
        const cutoff = new Date(Date.now() - days * 86400000).toISOString().split('T')[0];
        const journals = (jrnRes.value.data || []).filter(j => {
          const d = (j.created_at || j.date || '').slice(0, 10);
          return d >= cutoff;
        });
        journals.forEach(j => {
          allEvents.push({
            type: 'journal',
            date: (j.created_at || j.date || '').slice(0, 10),
            preview: j.content ? j.content.slice(0, 220) : null,
          });
        });
      }

      // Microdose (only if enabled)
      if (showMicrodose && mRes && mRes.status === 'fulfilled' && mRes.value?.data?.calendar) {
        const calDays = mRes.value.data.calendar || [];
        const protocol = mRes.value.data.protocol;
        calDays.filter(d => d.is_dosing_day).forEach(d => {
          allEvents.push({
            type: 'microdose',
            date: d.date,
            is_dose_day: true,
            protocol: protocol?.protocol_type
              ? `${protocol.protocol_type.charAt(0).toUpperCase() + protocol.protocol_type.slice(1)} protocol`
              : 'Active protocol',
          });
        });
      }

      allEvents.sort((a, b) => b.date > a.date ? 1 : -1);
      setEvents(allEvents);
    } catch (e) {
      console.error('Timeline load error:', e);
    } finally {
      setLoading(false);
      setTimeout(() => setIsVisible(true), 60);
    }
  };

  // Group by date
  const grouped = events.reduce((acc, ev) => {
    const d = ev.date?.slice(0, 10) || 'unknown';
    if (!acc[d]) acc[d] = [];
    acc[d].push(ev);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort((a, b) => b > a ? 1 : -1);

  const sessionCount = events.filter(e => e.type === 'session').length;
  const journalCount = events.filter(e => e.type === 'journal').length;
  const doseCount    = events.filter(e => e.type === 'microdose').length;

  // Streak: consecutive days with at least one event (most recent first)
  const streak = (() => {
    if (sortedDates.length === 0) return 0;
    let count = 0;
    let cursor = new Date();
    cursor.setHours(0, 0, 0, 0);
    for (const dateStr of sortedDates) {
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      const diff = Math.round((cursor - d) / 86400000);
      if (diff <= 1) { count++; cursor = d; }
      else break;
    }
    return count;
  })();

  const statsItems = [
    { label: 'Sessions', value: sessionCount, color: '#1E6B68', bg: 'rgba(30,107,104,0.08)' },
    { label: 'Journal', value: journalCount, color: '#4AACA8', bg: 'rgba(74,172,168,0.08)' },
    { label: 'Streak', value: `${streak}d`, color: '#B8906A', bg: 'rgba(184,144,106,0.08)' },
    ...(showMicrodose ? [{ label: 'Doses', value: doseCount, color: '#8B6BAE', bg: 'rgba(139,107,174,0.08)' }] : []),
  ];

  const legendItems = [
    { color: '#1E6B68', label: 'Session' },
    { color: '#4AACA8', label: 'Journal' },
    ...(showMicrodose ? [{ color: '#8B6BAE', label: 'Microdose' }] : []),
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F3EC 0%, #EFE7DD 55%, rgba(184,211,207,0.15) 100%)',
      padding: '24px 20px 100px',
      maxWidth: 600, margin: '0 auto',
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      transition: 'opacity 0.45s ease, transform 0.45s ease',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>

      {/* Back + header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          onClick={() => navigate('/insights')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#1E6B68', fontSize: '1.1rem' }}
        >←</button>
        <div>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '1.5rem', color: '#1E3A39',
            margin: 0, fontWeight: '500',
          }}>Emotional Timeline</h1>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#8FA3A0' }}>Your nervous system, day by day.</p>
        </div>
      </div>

      {/* Day range toggle */}
      <div style={{
        display: 'flex', gap: 6, marginBottom: 20,
        background: 'rgba(255,255,255,0.7)',
        padding: 5, borderRadius: 14,
        border: '1px solid rgba(184,211,207,0.3)',
        width: 'fit-content',
      }}>
        {[14, 30, 60, 90].map(d => (
          <button
            key={d}
            onClick={() => setDays(d)}
            style={{
              padding: '7px 16px',
              background: days === d ? '#1E6B68' : 'transparent',
              color: days === d ? '#fff' : '#7A9E9B',
              border: 'none', borderRadius: 10,
              fontSize: '0.8rem', fontWeight: '600',
              cursor: 'pointer', transition: 'all 0.2s',
            }}
          >{d}d</button>
        ))}
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {statsItems.map(s => (
          <div key={s.label} style={{
            flex: 1, background: s.bg,
            borderRadius: 12, padding: '10px 8px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.15rem', fontWeight: '700', color: s.color, fontFamily: "'Playfair Display',serif" }}>
              {s.value}
            </div>
            <div style={{ fontSize: '0.6rem', color: '#AABAB8', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: '600' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Sparkline */}
      {sparkData.length >= 2 && (
        <div style={{
          background: 'rgba(255,255,255,0.8)',
          border: '1px solid rgba(184,211,207,0.3)',
          borderRadius: 16, padding: '14px 18px',
          marginBottom: 20,
        }}>
          <div style={{ fontSize: '0.65rem', color: '#AAB8B6', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: '600', marginBottom: 8 }}>
            Activation · last {days} days
          </div>
          <Sparkline points={sparkData} width={Math.min(500, (typeof window !== 'undefined' ? window.innerWidth : 400) - 80)} height={52} />
        </div>
      )}

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        {legendItems.map(l => (
          <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: l.color }} />
            <span style={{ fontSize: '0.72rem', color: '#7A9E9B' }}>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Timeline */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 0' }}>
          <div style={{
            width: 36, height: 36,
            border: '2px solid rgba(30,107,104,0.15)',
            borderTop: '2px solid #1E6B68',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
        </div>
      ) : sortedDates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <img src={orbUrl} alt="" style={{ width: 60, opacity: 0.5, marginBottom: 16 }} />
          <p style={{ color: '#AABAB8', fontSize: '0.88rem', lineHeight: 1.7 }}>
            No entries in the last {days} days.<br />
            Complete a session or write a journal note to start your timeline.
          </p>
          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: 16, padding: '10px 24px',
              background: '#1E6B68', color: '#fff',
              border: 'none', borderRadius: 20,
              fontSize: '0.85rem', fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Start today's session →
          </button>
        </div>
      ) : (
        <div>
          {sortedDates.map(date => (
            <DateGroup key={date} dateStr={date} events={grouped[date]} />
          ))}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
